// src/components/Quiz.tsx

import React, { useState, useEffect } from 'react';
import GuessInput from './GuessInput';
import Notification from './Notification';
import Confetti from 'react-confetti';
import answerChecker from '../utils/answerChecker';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useStatistics } from './StatisticsContext';
import _ from 'lodash';
import moment from 'moment-timezone';

const QuizContainer = styled.div`
    display: flex;
    justify-content: center;
    padding: 20px;
    background-color: ${({ theme }) => theme.background};
    min-height: 100vh;
`;

const ContentWrapper = styled.div`
    width: 100%;
    max-width: 500px;
`;

const HeaderContainer = styled.div`
    margin-bottom: 20px;
    position: relative;
`;

const SubTitle = styled.p`
    font-size: 0.9rem;
    color: ${({ theme }) => theme.text};
    margin: 4px 0 0 0;
    text-align: center;
`;

const CluesContainer = styled.div`
    display: flex;
    flex-direction: column;
`;

interface ClueCardProps {
    isRevealed: boolean;
    theme: any;
}

const ClueCard = styled(motion.div)<ClueCardProps>`
    margin-top: 12px;
    padding: 20px;
    background-color: ${({ theme }) => theme.tileEmpty};
    color: ${({ theme }) => theme.text};
    border-radius: 8px;
    width: 100%;
    box-sizing: border-box;
    display: flex;
    align-items: center;
    opacity: ${({ isRevealed }) => (isRevealed ? 1 : 0)};
    transition: opacity 0.5s ease-in-out;
`;

const ClueNumber = styled.div`
    font-size: 1.2rem;
    font-weight: bold;
    background-color: ${({ theme }) => theme.accentColor};
    color: #ffffff;
    width: 40px;
    height: 40px;
    min-width: 40px;
    min-height: 40px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-right: 16px;
`;

const ClueText = styled.div`
    flex: 1;
    font-size: 1rem;
    color: ${({ theme }) => theme.text};
`;

const ButtonContainer = styled.div`
    width: 100%;
`;

const NextHintButton = styled.button`
    width: 100%;
    height: 52px;
    font-size: 1em;
    font-weight: bold;
    font-family: inherit;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: ${({ theme }) => theme.text};
    background-color: ${({ theme }) => theme.accentColor};
    border: none;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.2s ease;
    margin-top: 12px;

    &:hover:not(:disabled) {
        background-color: #6aaa64;
    }

    &:disabled {
        background-color: ${({ theme }) => theme.borderColor};
        cursor: not-allowed;
        opacity: 0.6;
    }
`;

const ShakeAnimation = styled.div`
    &.shake {
        animation: shake 0.5s;
    }
`;

const Message = styled.p`
    color: ${({ theme }) => theme.text};
    font-size: 1em;
    text-align: center;
    margin-top: 20px;
`;

const InfoMessage = styled.p`
    color: ${({ theme }) => theme.text};
    font-size: 1em;
    text-align: center;
    margin-top: 20px;
`;

const ResultContainer = styled(motion.div)`
  margin-top: 20px;
  margin-bottom: 40px;
  padding: 20px;
  background-color: ${({ theme }) => theme.tileEmpty};
  color: ${({ theme }) => theme.text};
  border-radius: 8px;
  text-align: center;
  width: 100%;
  box-sizing: border-box;
`;

interface Clue {
    text: string;
}

interface Quiz {
    date: string;
    gameNumber: number;
    correctAnswer: string;
    answerVariants: string[];
    acceptableKeywords: string[];
    clues: Clue[];
    category?: string;
    difficulty?: string;
    tags?: string[];
    imageUrl?: string;
}

const QuizComponent: React.FC = () => {
    const [quiz, setQuiz] = useState<Quiz | null>(null);
    const [guesses, setGuesses] = useState<string[]>([]);
    const [revealedClues, setRevealedClues] = useState<boolean[]>([]);
    const [revealedDuringQuizClues, setRevealedDuringQuizClues] = useState<boolean[]>([]);
    const [feedback, setFeedback] = useState<string>('');
    const [gameOver, setGameOver] = useState<boolean>(false);
    const [showNotification, setShowNotification] = useState<boolean>(false);
    const [showConfetti, setShowConfetti] = useState<boolean>(false);
    const [shake, setShake] = useState<boolean>(false);
    const [gamePlayed, setGamePlayed] = useState<boolean>(false);
    const [isCorrectGuess, setIsCorrectGuess] = useState<boolean | null>(null);
    const [modelLoaded, setModelLoaded] = useState<boolean>(false);
    const [overrideUsed, setOverrideUsed] = useState<boolean>(false);
    const [showOverrideButton, setShowOverrideButton] = useState<boolean>(false);
    const [isWeekend, setIsWeekend] = useState<boolean>(false); // New state
    const [infoMessage, setInfoMessage] = useState<string>(''); // New state

    const { updateStatistics } = useStatistics();

    useEffect(() => {
        // Load the USE model when the component mounts
        const loadModel = async () => {
            await answerChecker('', [], []);
            setModelLoaded(true);
        };
        loadModel();
    }, []);

    useEffect(() => {
        const fetchQuiz = async () => {
            try {
                const response = await fetch('/clues.json');
                if (!response.ok) {
                    throw new Error('Failed to fetch quiz data.');
                }
                const data: Quiz = await response.json();

                const today = moment().tz('America/New_York');
                let todayAdjustedForOffset = today.subtract(3, 'hours').subtract(5, 'minutes');
                let quizDate = todayAdjustedForOffset;

                // Check if today is Saturday (6) or Sunday (7)
                const isoWeekday = todayAdjustedForOffset.isoWeekday();
                if (isoWeekday === 6 || isoWeekday === 7) {
                    setIsWeekend(true);
                    setInfoMessage('The next new riddle ravel is on Monday.');
                    // For weekends, get Friday's quiz
                    quizDate = today.subtract(isoWeekday - 5, 'days').subtract(3, 'hours').subtract(5, 'minutes'); // Adjust to Friday
                } else {
                    setIsWeekend(false);
                }

                const currentQuizDate = quizDate.format('YYYY-MM-DD');
                console.log(JSON.stringify(data))
                if (data.date !== currentQuizDate) {
                    throw new Error('No game available for today.');
                }

                setQuiz(data);
                setRevealedClues(Array(data.clues.length).fill(false));
                setRevealedDuringQuizClues(Array(data.clues.length).fill(false));
                const storedDate = localStorage.getItem('quizDate');
                const storedGameData = localStorage.getItem('gameData');
                const storedOverrideUsed = localStorage.getItem('overrideUsed');

                if (storedDate === data.date && storedGameData) {
                    const gameData = JSON.parse(storedGameData);
                    setGuesses(gameData.guesses);
                    setFeedback(gameData.feedback);
                    setGamePlayed(true);
                    setGameOver(true);
                    setRevealedClues(Array(data.clues.length).fill(true));
                    setRevealedDuringQuizClues(Array(data.clues.length).fill(true));
                    setIsCorrectGuess(gameData.isCorrectGuess);
                    setOverrideUsed(storedOverrideUsed === 'true');
                } else {
                    // Reset overrideUsed for a new day
                    setOverrideUsed(false);
                    localStorage.removeItem('overrideUsed');
                }
            } catch (error) {
                console.error('Error fetching quiz:', error);
                setQuiz(null);
            }
        };

        fetchQuiz();
    }, []);

    const handleGuessSubmit = async (guess: string) => {
        if (gameOver || !quiz) return;

        if (!modelLoaded) {
            setFeedback('Model is still loading. Please wait a moment and try again.');
            setShowNotification(true);
            setTimeout(() => setShowNotification(false), 3000);
            return;
        }

        setGuesses([guess]);
        setShowNotification(false);
        setFeedback('Checking...');

        try {
            const allCorrectAnswers = [quiz.correctAnswer, ...quiz.answerVariants];
            const acceptableKeywords = quiz.acceptableKeywords || [];

            const isCorrect = await answerChecker(guess, allCorrectAnswers, acceptableKeywords);

            const feedbackMessage = isCorrect ? 'Correct!' : 'Incorrect.';
            setFeedback(feedbackMessage);
            setShowNotification(true);
            setIsCorrectGuess(isCorrect);

            if (isCorrect) {
                setShowConfetti(true);
                setRevealedDuringQuizClues(_.cloneDeep(revealedClues));
                setRevealedClues(revealedClues.map(() => true));
            } else {
                setShake(true);
                setRevealedDuringQuizClues(_.cloneDeep(revealedClues));
                setRevealedClues(revealedClues.map(() => true));
                setShowOverrideButton(true); // Show override button
            }

            setGameOver(true);
            localStorage.setItem('quizDate', quiz.date);
            localStorage.setItem(
                'gameData',
                JSON.stringify({
                    guesses: [guess],
                    feedback: feedbackMessage,
                    isCorrectGuess: isCorrect,
                })
            );

            const cluesUsed = revealedClues.filter((r) => r).length;
            updateStatistics(isCorrect, cluesUsed, quiz.clues.length);

            setTimeout(() => setShowNotification(false), 3000);
        } catch (error) {
            console.error('Error checking answer:', error);
            setFeedback('An error occurred. Please try again.');
            setShowNotification(true);
            setTimeout(() => setShowNotification(false), 3000);
        }
    };

    const handleNextClue = () => {
        if (!quiz) return;
        const nextClueIndex = revealedClues.findIndex((revealed) => !revealed);
        if (nextClueIndex !== -1) {
            const updatedRevealedClues = [...revealedClues];
            updatedRevealedClues[nextClueIndex] = true;
            setRevealedClues(updatedRevealedClues);
        }
    };

    const handleOverride = () => {
        if (overrideUsed) return;

        setIsCorrectGuess(true);
        setFeedback('Your answer has been marked as correct.');
        setShowNotification(true);
        setShowConfetti(true);
        setOverrideUsed(true);
        setShowOverrideButton(false);

        // Update statistics: Adjust the previous game result
        const cluesUsed = revealedDuringQuizClues.filter((r) => r).length;
        updateStatistics(true, cluesUsed, quiz?.clues.length || 0, true); // Pass isOverride = true

        // Update local storage
        localStorage.setItem('overrideUsed', 'true');
        localStorage.setItem(
            'gameData',
            JSON.stringify({
                guesses: guesses,
                feedback: 'Your answer has been marked as correct.',
                isCorrectGuess: true,
            })
        );

        setTimeout(() => setShowNotification(false), 3000);
    };


    useEffect(() => {
        if (shake) {
            const timer = setTimeout(() => setShake(false), 500);
            return () => clearTimeout(timer);
        }
    }, [shake]);

    if (!quiz) {
        return (
            <QuizContainer>
                {isWeekend ? (
                    <InfoMessage>{infoMessage}</InfoMessage>
                ) : (
                    <Message>No Riddle Ravel is currently available. Please check back shortly.</Message>
                )}
            </QuizContainer>
        );
    }

    return (
        <QuizContainer>
            <ContentWrapper>
                <HeaderContainer>{/* Header content if any */}</HeaderContainer>
                <SubTitle>
                    {quiz.date} - Riddle Ravel #{quiz.gameNumber}
                </SubTitle>
                {gamePlayed ? (
                    <>
                        <Message>
                            You have already played today's Riddle Ravel.
                        </Message>
                        <ResultContainer
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ duration: 0.5 }}
                        >
                            <p>
                                Your answer: <strong>{guesses[0]}</strong>
                            </p>
                            <p>
                                {isCorrectGuess
                                    ? 'You got it right! 🎉'
                                    : 'Sorry, that was incorrect.'}
                            </p>
                            <p>
                                The correct answer was: <strong>{quiz.correctAnswer}</strong>
                            </p>
                        </ResultContainer>
                        <CluesContainer>
                            {quiz.clues.map((clue, index) => (
                                <ClueCard
                                    key={index}
                                    isRevealed={true}
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    transition={{ duration: 0.5 }}
                                >
                                    <ClueNumber>{index + 1}</ClueNumber>
                                    <ClueText>{clue.text}</ClueText>
                                </ClueCard>
                            ))}
                        </CluesContainer>
                    </>
                ) : (
                    <>
                        <ShakeAnimation className={shake ? 'shake' : ''}>
                            <CluesContainer>
                                {quiz.clues.map((clue, index) => {
                                    const isRevealed = revealedClues[index];
                                    return (
                                        <ClueCard
                                            key={index}
                                            isRevealed={isRevealed}
                                            initial={{ opacity: 0 }}
                                            animate={{ opacity: isRevealed ? 1 : 0 }}
                                            transition={{ duration: 0.5 }}
                                        >
                                            {isRevealed && (
                                                <>
                                                    <ClueNumber>{index + 1}</ClueNumber>
                                                    <ClueText>{clue.text}</ClueText>
                                                </>
                                            )}
                                        </ClueCard>
                                    );
                                })}
                            </CluesContainer>
                        </ShakeAnimation>
                        {!gameOver && (
                            <>
                                <GuessInput onSubmit={handleGuessSubmit} />
                                <ButtonContainer>
                                    <NextHintButton
                                        onClick={handleNextClue}
                                        disabled={!revealedClues.includes(false)}
                                    >
                                        {revealedClues.includes(true) ? 'Next Hint' : 'First Hint'}
                                    </NextHintButton>
                                </ButtonContainer>
                            </>
                        )}
                        {gameOver && (
                            <ResultContainer
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ duration: 0.5 }}
                            >
                                <p>
                                    Your answer: <strong>{guesses[0]}</strong>
                                </p>
                                <p>
                                    {isCorrectGuess
                                        ? 'You got it right! 🎉'
                                        : 'Sorry, that was incorrect.'}
                                </p>
                                <p>
                                    The correct answer was: <strong>{quiz.correctAnswer}</strong>
                                </p>
                                {showOverrideButton && !overrideUsed && (
                                    <OverrideButton onClick={handleOverride}>
                                        I think my answer is correct
                                    </OverrideButton>
                                )}
                            </ResultContainer>
                        )}
                        {showNotification && (
                            <Notification message={feedback} isSuccess={feedback.includes('correct')} />
                        )}
                        {showConfetti && <Confetti />}
                    </>
                )}
            </ContentWrapper>
        </QuizContainer>
    );
};

// Uncomment the following function to allow quizzes every day, including weekends
/*
const getQuizDate = () => {
  const today = moment().tz('America/New_York');
  return today.format('YYYY-MM-DD');
};
*/

const OverrideButton = styled.button`
  margin-top: 12px;
  padding: 10px 20px;
  background-color: ${({ theme }) => theme.accentColor};
  color: ${({ theme }) => theme.text};
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 1em;
  font-weight: bold;

  &:hover {
    background-color: #6aaa64;
  }

  &:disabled {
    background-color: ${({ theme }) => theme.borderColor};
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

export default QuizComponent;
